// =============================================
// VALIDAZIONI BUSINESS RULES
// =============================================

export const DURATE_DISPONIBILI = [
  { value: 12, label: '12 mesi' },
  { value: 24, label: '24 mesi' },
  { value: 36, label: '36 mesi' },
  { value: 48, label: '48 mesi' },
  { value: 60, label: '60 mesi' },
]

// Tre macro-categorie di pratica.
// - concessionari: polizze CVT vendute dai concessionari (dealer + annuali, 12-48 mesi)
// - annuali:      polizze annuali (solo annuali, 12 mesi fissi)
// - autocarri_intermediari: polizze autocarri dedicate agli intermediari/subagenti
// - solo_standalone: vendita di sole garanzie stand-alone
export const TIPI_OPERAZIONE = [
  {
    value: 'concessionari',
    label: 'Polizze concessionari',
    desc: 'Polizze CVT poliennali per veicoli venduti dal concessionario (12, 24, 36 o 48 mesi). Include anche le annuali.',
    ruoli: ['dealer'],
  },
  {
    value: 'annuali',
    label: 'Polizze annuali',
    desc: 'Polizze CVT a durata 12 mesi.',
    // Solo subagente: per i dealer le annuali si fanno scegliendo "Polizze concessionari"
    // (le compagnie annuali sono già incluse nel mix di concessionari).
    ruoli: ['subagente'],
  },
  {
    value: 'autocarri_intermediari',
    label: 'Polizze autocarri',
    desc: 'Polizze annuali dedicate agli intermediari per autocarri fino e oltre 35 quintali.',
    ruoli: ['admin'],
  },
  {
    value: 'solo_standalone',
    label: 'Garanzie stand alone',
    desc: 'Vendita di sole garanzie stand-alone (satellitare, Cristalli, ecc.) senza CVT.',
    ruoli: ['dealer', 'subagente'],
  },
]

// Macro-tipi visibili a un dato ruolo.
// L'admin (o ruoli non riconosciuti) vede tutte le opzioni.
export function tipiOperazioneAmmessi(ruolo) {
  if (!ruolo || ruolo === 'admin') return TIPI_OPERAZIONE
  const filtrati = TIPI_OPERAZIONE.filter(t => t.ruoli.includes(ruolo))
  // Safety net: se per qualunque motivo il ruolo non matcha nessun tipo,
  // mostra comunque tutto invece di lasciare il wizard bloccato.
  return filtrati.length > 0 ? filtrati : TIPI_OPERAZIONE
}

// Mapper retro-compatibile per bozze salvate prima del refactor.
// rinnovo → annuali, nuova_emissione/voltura → concessionari.
export function mappaTipoOperazioneLegacy(tipo) {
  if (tipo === 'rinnovo') return 'annuali'
  if (tipo === 'nuova_emissione' || tipo === 'voltura') return 'concessionari'
  return tipo
}

// Indica se il flusso polizza (veicolo, compagnie, comparazione) è applicabile.
// Per solo_standalone il flusso si riduce a cliente + selezione stand-alone.
export function richiedePolizza(tipoOp) {
  return tipoOp && tipoOp !== 'solo_standalone'
}

export function isAutocarriIntermediari(tipoOp) {
  return tipoOp === 'autocarri_intermediari'
}

// Le polizze annuali hanno durata fissa 12 mesi.
// Anche la causale "rinnovo" forza 12 mesi (un rinnovo per definizione copre
// l'annualità in scadenza).
export function durataFissa(tipoOp, causale) {
  if (tipoOp === 'autocarri_intermediari') return 12
  if (tipoOp === 'annuali') return 12
  if (causale === 'rinnovo') return 12
  return null
}

export const DURATE_STANDALONE = [
  { value: 1, label: '1 anno' },
  { value: 2, label: '2 anni' },
  { value: 3, label: '3 anni' },
  { value: 4, label: '4 anni' },
  { value: 5, label: '5 anni' },
]

/**
 * Restituisce le categorie compagnia ammesse per (ruolo, tipo_operazione).
 * - concessionari (solo dealer): annuali + dealer (poliennali)
 * - annuali (dealer/subagente): solo annuali
 * - solo_standalone: nessuna compagnia (flusso ridotto)
 */
export function categorieCompagnieAmmesse(ruolo, tipoOperazione) {
  if (tipoOperazione === 'autocarri_intermediari') return ['intermediari']
  // Nuovi macro-tipi.
  // La categoria 'mista' (COVEA, QUIXA, SOGESSUR) è applicabile sia a
  // polizze poliennali (concessionari) sia annuali (durata forzata a 12).
  if (tipoOperazione === 'annuali') return ['annuale', 'mista']
  if (tipoOperazione === 'concessionari') {
    if (ruolo === 'subagente') return ['annuale', 'mista']  // safety net
    return ['annuale', 'dealer', 'mista']
  }
  // Legacy fallback (bozze pre-refactor): comportamento storico
  if (tipoOperazione === 'rinnovo') return ['annuale', 'mista']
  if (tipoOperazione === 'nuova_emissione' || tipoOperazione === 'voltura') {
    if (ruolo === 'subagente') return ['annuale', 'mista']
    return ['annuale', 'dealer', 'mista']
  }
  return []
}

/**
 * Lista esplicita di codici compagnia ammessi per scenario (ruolo × macro × causale).
 * Più stretta di `categorieCompagnieAmmesse`: filtra le compagnie per caso d'uso
 * specifico, secondo le regole business del committente.
 *
 * Il filtro territoriale (Lloyd's Volante solo Campania, esclusioni Lazio/Campania
 * su Covea/Quixa/Sogessur) viene applicato successivamente da `compagniaApplicabile`.
 *
 * Restituisce null = "nessun filtro per scenario, usa categorieCompagnieAmmesse".
 */
export function compagnieAmmessePerScenario(ruolo, tipoOperazione, causale) {
  if (tipoOperazione === 'solo_standalone') return []
  if (tipoOperazione === 'autocarri_intermediari') return ['AUTOCARRI_INTERMEDIARI']

  // Pratiche legacy: prima del refactor il rinnovo era anche un tipo_operazione
  // autonomo (non una causale su "concessionari" / "annuali").
  if (tipoOperazione === 'rinnovo') {
    return ['EIR', 'LLOYD_PLUS', 'QUIXA', 'SOGESSUR']
  }

  // Admin riusa il filtro del dealer (vede tutto il network)
  const ruoloEff = ruolo === 'admin' ? 'dealer' : ruolo

  // CONCESSIONARI (solo dealer)
  if (tipoOperazione === 'concessionari') {
    if (ruoloEff !== 'dealer') return null  // safety net, ricade su categorie
    if (causale === 'rinnovo') {
      return ['EIR', 'QUIXA', 'SOGESSUR', 'LLOYD_PLUS']
    }
    // Nuova o Usata (default se causale non ancora scelta)
    return ['EIR', 'LLOYD_PLUS', 'LLOYD_VOLANTE', 'QUIXA', 'SOGESSUR', 'COVEA']
  }

  // ANNUALI (solo subagente, perché ai dealer non viene mostrata)
  if (tipoOperazione === 'annuali') {
    if (causale === 'rinnovo') {
      return ['EIR', 'LLOYD_PLUS', 'QUIXA', 'SOGESSUR']
    }
    // Nuova o Usata
    return ['EIR', 'COVEA', 'LLOYD_PLUS', 'QUIXA', 'SOGESSUR']
  }

  return null
}

export const PACCHETTI = [
  { value: 'BASE', label: 'BASE - Furto/Incendio + Garanzie Accessorie' },
  { value: 'PLUS', label: 'PLUS - BASE + Atti vandalici + Eventi atmosferici' },
]

export const PESI_AUTOCARRO = [
  { value: 'fino_35', label: 'FINO A 35 QUINTALI' },
  { value: 'oltre_35', label: 'OLTRE I 35 QUINTALI' },
]

export const PACCHETTI_AUTOCARRO = [
  {
    value: 'ECONOMICO',
    label: 'Economico',
    garanzie: ['Furto', 'Incendio', 'Ricorso Terzi da Incendio'],
  },
  {
    value: 'EQUILIBRATO',
    label: 'Equilibrato',
    garanzie: ['Furto', 'Incendio', 'Atti vandalici', 'Eventi naturali', 'Ricorso Terzi da Incendio'],
  },
  {
    value: 'COMPLETO',
    label: 'Completo',
    garanzie: ['Furto', 'Incendio', 'Atti vandalici', 'Eventi naturali', 'Ricorso Terzi da Incendio', 'Cristalli'],
  },
]

export const MASSIMALI_CRISTALLI_AUTOCARRO = [
  { value: 650, label: '650 euro', extra: 110 },
  { value: 750, label: '750 euro', extra: 160 },
  { value: 1000, label: '1000 euro', extra: 220 },
]

// Causale della pratica (step "Dati comuni"). Sostituisce stato_veicolo nella UI:
// stato_veicolo viene derivato (nuova → nuovo, usata/rinnovo → usato).
export const CAUSALI = [
  { value: 'nuova',   label: 'Nuova',   desc: 'Prima immatricolazione' },
  { value: 'usata',   label: 'Usata',   desc: 'Auto usata acquistata (voltura)' },
  { value: 'rinnovo', label: 'Rinnovo', desc: 'Veicolo già assicurato in scadenza' },
]

export function causaleToStatoVeicolo(causale) {
  if (causale === 'nuova') return 'nuovo'
  if (causale === 'usata' || causale === 'rinnovo') return 'usato'
  return null
}

// Massimo numero di giorni dalla prima immatricolazione entro cui un veicolo
// può ancora essere classificato come "Nuova". Oltre questa soglia l'auto
// passa automaticamente a "usata".
export const GIORNI_MAX_NUOVA = 30

// Controlla che la causale 'nuova' sia coerente con la data di immatricolazione.
// Restituisce { ok, msg } con msg popolato solo in caso di errore.
export function validaCausaleNuova(causale, immatricolazione) {
  if (causale !== 'nuova') return { ok: true }
  if (!immatricolazione) return { ok: true }  // niente data → non posso giudicare
  const d = new Date(immatricolazione)
  if (isNaN(d.getTime())) return { ok: true }
  const oggi = new Date()
  const giorniFloor = Math.floor((oggi - d) / (1000 * 60 * 60 * 24))
  if (giorniFloor > GIORNI_MAX_NUOVA) {
    return {
      ok: false,
      giorni: giorniFloor,
      msg: `Il veicolo è stato immatricolato ${giorniFloor} giorni fa: oltre ${GIORNI_MAX_NUOVA} giorni non può più essere classificato come "Nuova". Seleziona "Usata" o "Rinnovo".`,
    }
  }
  return { ok: true, giorni: giorniFloor }
}

// Simmetrico al check Nuova: una causale 'rinnovo' presuppone una polizza
// preesistente, quindi il veicolo deve essere usato (immatricolato da almeno
// 30 giorni). Su veicoli appena immatricolati va usato "Nuova".
export function validaCausaleRinnovo(causale, immatricolazione) {
  if (causale !== 'rinnovo') return { ok: true }
  if (!immatricolazione) return { ok: true }
  const d = new Date(immatricolazione)
  if (isNaN(d.getTime())) return { ok: true }
  const oggi = new Date()
  const giorniFloor = Math.floor((oggi - d) / (1000 * 60 * 60 * 24))
  if (giorniFloor <= GIORNI_MAX_NUOVA) {
    return {
      ok: false,
      giorni: giorniFloor,
      msg: `Il veicolo è stato immatricolato ${giorniFloor} giorni fa: per i rinnovi serve un veicolo già usato (>${GIORNI_MAX_NUOVA} giorni dall'immatricolazione). Seleziona "Nuova" se è una prima immatricolazione.`,
    }
  }
  return { ok: true, giorni: giorniFloor }
}

// Solo per retro-compatibilità con bozze legacy salvate con il vecchio
// `tipo_operazione` (rinnovo/nuova_emissione/voltura): inferisce la causale.
export function inferiCausaleDaLegacy(legacyTipo) {
  if (legacyTipo === 'rinnovo') return 'rinnovo'
  if (legacyTipo === 'nuova_emissione') return 'nuova'
  if (legacyTipo === 'voltura') return 'usata'
  return ''
}

export const STATI_VEICOLO = [
  { value: 'nuovo', label: 'Nuovo' },
  { value: 'usato', label: 'Usato' },
]

// Tipologia veicolo (auto vs autocarro). Per gli autocarri vale il limite
// massa massima 35 quintali (3500 kg). Al momento non differenzia il calcolo
// tariffario — è solo un dato anagrafico mostrato all'utente.
export const TIPI_VEICOLO = [
  { value: 'auto',      label: 'Auto' },
  { value: 'autocarro', label: 'Autocarro', hint: 'Massa massima ammessa: 35 quintali (3.500 kg)' },
]

export function valoreAutoValido(v) {
  const n = parseFloat(v)
  if (isNaN(n) || n <= 0) return { ok: false, msg: 'Valore auto non valido' }
  if (n > 200000) return { ok: false, msg: 'Valore massimo 200.000 EUR' }
  return { ok: true }
}

export function valoreVeicoloValido(v, tipoOperazione) {
  const n = parseFloat(v)
  if (isNaN(n) || n <= 0) return { ok: false, msg: 'Valore veicolo non valido' }
  if (tipoOperazione === 'autocarri_intermediari') {
    if (n > 300000) return { ok: false, msg: 'Valore superiore a 300.000 EUR: veicolo non assicurabile' }
    return { ok: true }
  }
  return valoreAutoValido(v)
}

export function eta_immatricolazione_valida(dataImmatricolazione) {
  if (!dataImmatricolazione) return { ok: false, msg: 'Data immatricolazione mancante' }
  const d = new Date(dataImmatricolazione)
  const now = new Date()
  const anni = (now - d) / (1000*60*60*24*365.25)
  if (anni > 10) return { ok: false, msg: 'Veicoli con piu di 10 anni dall\'immatricolazione non assicurabili' }
  return { ok: true, anni }
}

export function validaPraticaCompleta(p) {
  const errs = []
  if (!p.cliente_nome) errs.push('Nome cliente mancante')
  if (!p.cliente_cognome) errs.push('Cognome cliente mancante')

  // Flusso "solo stand-alone": serve almeno uno SA selezionato + veicolo + contatti
  if (p.tipo_operazione === 'solo_standalone') {
    if (!Array.isArray(p.standalone_selezionati) || p.standalone_selezionati.length === 0) {
      errs.push('Selezionare almeno uno stand-alone')
    }
    const dc = p.dati_comuni || {}
    if (!dc.targa || !String(dc.targa).trim()) errs.push('Targa veicolo mancante')
    if (!dc.marca_veicolo || !String(dc.marca_veicolo).trim()) errs.push('Marca veicolo mancante')
    if (!dc.modello_veicolo || !String(dc.modello_veicolo).trim()) errs.push('Modello veicolo mancante')
    if (!dc.cellulare_contraente || !/^[+\d][\d\s().\/-]{7,19}$/.test(dc.cellulare_contraente)) {
      errs.push('Cellulare contraente mancante o non valido')
    }
    if (!dc.email_contraente || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dc.email_contraente)) {
      errs.push('Email contraente mancante o non valida')
    }
    return errs
  }

  // Flusso polizza standard
  const dc = p.dati_comuni || {}
  if (!dc.provincia) errs.push('Provincia mancante')
  if (!dc.valore_auto) errs.push('Valore auto mancante')
  if (p.tipo_operazione === 'autocarri_intermediari') {
    const valoreCheck = valoreVeicoloValido(dc.valore_auto, p.tipo_operazione)
    if (!valoreCheck.ok) errs.push(valoreCheck.msg)
    if (dc.categoria_veicolo !== 'autocarro') errs.push('Tipologia veicolo deve essere autocarro')
    if (!['fino_35', 'oltre_35'].includes(dc.peso_autocarro)) errs.push('Peso autocarro mancante')
    if (parseInt(dc.durata_mesi, 10) !== 12) errs.push('Durata autocarri intermediari deve essere 12 mesi')
    if (!['ECONOMICO', 'EQUILIBRATO', 'COMPLETO'].includes(dc.pacchetto_autocarro)) errs.push('Pacchetto autocarro mancante')
    if (dc.pacchetto_autocarro === 'COMPLETO' && ![650, 750, 1000].includes(parseInt(dc.cristalli_massimale, 10))) {
      errs.push('Massimale cristalli mancante')
    }
  }
  if (!dc.durata_mesi) errs.push('Durata mancante')
  if (!dc.causale) errs.push('Causale mancante (Nuova / Usata / Rinnovo)')
  if (!dc.stato_veicolo) errs.push('Stato veicolo mancante')
  if (!dc.pacchetto) errs.push('Pacchetto mancante')
  // Vincolo: rinnovo = durata 12 mesi obbligatoria
  if (dc.causale === 'rinnovo' && parseInt(dc.durata_mesi, 10) !== 12) {
    errs.push('Per i rinnovi la durata deve essere 12 mesi')
  }
  // Vincolo: rinnovo serve auto usata (>30gg dall'immatricolazione)
  const vR = validaCausaleRinnovo(dc.causale, dc.immatricolazione)
  if (!vR.ok) errs.push(vR.msg)
  // Dati aggiuntivi raccolti allo step Documenti (obbligatori per stipula polizza)
  if (!dc.colore_auto || !String(dc.colore_auto).trim()) errs.push('Colore auto mancante')
  if (dc.km == null || isNaN(parseInt(dc.km)) || parseInt(dc.km) < 0) errs.push('Chilometri mancanti o non validi')
  if (!dc.cellulare_contraente || !/^[+\d][\d\s().\/-]{7,19}$/.test(dc.cellulare_contraente)) {
    errs.push('Cellulare contraente mancante o non valido')
  }
  if (!dc.email_contraente || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dc.email_contraente)) {
    errs.push('Email contraente mancante o non valida')
  }
  // Identificativi veicolo obbligatori
  if (!dc.targa || !String(dc.targa).trim()) errs.push('Targa veicolo mancante')
  if (!dc.marca_veicolo || !String(dc.marca_veicolo).trim()) errs.push('Marca veicolo mancante')
  if (!dc.modello_veicolo || !String(dc.modello_veicolo).trim()) errs.push('Modello veicolo mancante')
  return errs
}
